import { Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { catchError, map, switchMap } from "rxjs/operators";
import { ApiService } from "src/app/services/api.service";
import { DashboardService } from "src/app/services/dashboard.service";
import {
  FilterService,
  STATIC_BOOKMARK_FIELD,
} from "src/app/services/filter.service";
import { SessionService } from "src/app/services/session.service";
import { BookmarkService } from "src/app/components/shared/bookmark-view/bookmark.service";

// ===========================================================================
// API access for the DPM-Static dashboard.
//
// Everything this screen asks the backend for goes through here, so the
// component stays presentation-only and a second static dashboard can reuse the
// same request shapes. Two calls today, in this order:
//
//   1. the dynamic filters (Brand, via kpi/get-brand-filter)
//   2. the dashboard data (kpi/get-execution-details) for what they resolved to
//
// The order is a real dependency, not a convention: the execution call is
// brand-scoped, so it cannot be issued until a brand has been picked. See
// loadDynamicFilters().
// ===========================================================================

/**
 * The request context every DPM-Static call carries. Assembled by
 * buildContext() from state the portal already holds — nothing here is a
 * literal, so the payload follows the signed-in user, the selected client and
 * the dashboard actually on screen.
 */
export interface DpmStaticContext {
  dashboard_key: any;
  page_key?: any;
  client_key: any;
  user_id: any;
  employee_key: any;
  module_key: any;
}

/** The keys the component reads off its own route, passed in so the route wins
 *  over the (older) service-level copies of the same values. */
export interface DpmStaticRouteKeys {
  dashboardKey?: any;
  clientKey?: any;
  moduleKey?: any;
}

/** One option of a dynamic filter: what the user sees, what a request sends. */
export interface DpmFilterOption {
  /** Request value, e.g. brand_key. */
  key: any;
  /** Display value, and what the component's filter chips key off. */
  label: string;
  /** The untouched API row, for anything a caller needs beyond key/label. */
  raw: any;
}

/**
 * The order a filter's values are offered in: ascending, on both static screens
 * and in both places a filter is shown (the badge strip's dropdown and the
 * right-side panel's select).
 *
 * Neither source of options arrives in an order worth keeping. A data-backed
 * chip's values come out of distinct() in whatever order the records happened to
 * be in, so the same filter could list its values differently on two consecutive
 * loads of the same screen; an API-backed chip's come out in the order the
 * endpoint returned them, which is its own business and not a reading order.
 * Either way a list you have to scan for a value should be in the order you
 * would scan it.
 *
 * localeCompare with `numeric` rather than a bare `<`, for the two things a
 * string sort gets wrong on values like these: "Site 10" sorts after "Site 9"
 * instead of before it, and a lowercase value no longer lands after every
 * uppercase one (a plain sort is by code point, which puts "apple" after
 * "Zebra"). `base` sensitivity also folds accents, so "Zurich" and "Zürich" sit
 * together rather than at opposite ends.
 *
 * Copies rather than sorting in place: the arrays handed in are often ones a
 * caller still holds in its own order — DpmDynamicFilterState.options is the
 * clearest case, where the FIRST entry is the filter's default selection and
 * must stay the API's first, not the alphabet's.
 */
export function sortFilterValues(values: string[]): string[] {
  return [...values].sort((a, b) =>
    String(a).localeCompare(String(b), undefined, {
      numeric: true,
      sensitivity: "base",
    })
  );
}

/**
 * A filter whose options come from an API rather than from the loaded data.
 *
 * Adding one is a single entry in DPM_DYNAMIC_FILTERS below — the load sequence
 * itself does not change. Entries resolve in declaration order and each one can
 * name earlier entries in `dependsOn`, whose chosen values are then sent as
 * request parameters (so a Source filter scoped by Brand needs no new code).
 */
export interface DpmDynamicFilterDef {
  /** Matches the component filter chip this feeds (a record field name). */
  key: string;
  label: string;
  /** Single-select (like Brand) vs multi-select. */
  single: boolean;
  endpoint: string;
  /** Response field holding the request value. */
  valueField: string;
  /** Response field holding the display value. */
  labelField: string;
  /** Payload field this filter's selection is sent as, both to dependent
   *  filters and to kpi/get-execution-details. */
  requestField: string;
  /** Keys of filters that must resolve before this one can load. */
  dependsOn: string[];
}

export const DPM_DYNAMIC_FILTERS: DpmDynamicFilterDef[] = [
  {
    key: "brand_name",
    label: "Brand",
    single: true,
    endpoint: "kpi/get-brand-filter",
    valueField: "brand_key",
    labelField: "brand_name",
    requestField: "brand_key",
    dependsOn: [],
  },
  // Next dynamic filter slots in here, e.g. a brand-scoped source list:
  // { key: "source_name", label: "Source", single: false,
  //   endpoint: "kpi/get-source-filter", valueField: "source_key",
  //   labelField: "source_name", requestField: "source_key",
  //   dependsOn: ["brand_name"] },
];

/**
 * The same, for DEM-Static (Data Extract Monitor).
 *
 * Brand only, and identical to DPM's today — kept as its own list rather than
 * shared so the two screens' filter sets can diverge without one silently
 * changing the other, which is the whole point of naming them separately.
 *
 * The screen's second chip, Destination, is deliberately NOT here: it is the
 * extract's target (`source_name`, which the extract grid labels "Target Name"),
 * and every value it can take is already in the loaded records — so it is built
 * from the data the way DEM-Static's other non-brand chips are, and needs no
 * endpoint. Move it here if a destination list ever has to be offered before the
 * data arrives.
 */
export const DEM_DYNAMIC_FILTERS: DpmDynamicFilterDef[] = [
  {
    key: "brand_name",
    label: "Brand",
    single: true,
    endpoint: "kpi/get-brand-filter",
    valueField: "brand_key",
    labelField: "brand_name",
    requestField: "brand_key",
    dependsOn: [],
  },
];

/**
 * Selections the rest of the portal already holds, keyed by the payload field
 * they stand for. Brand is chosen once per module (dashboard.component's
 * setBrandKey) and every dashboard follows it, so opening this screen picks up
 * that brand instead of resetting to the first one.
 */
const SHARED_SELECTION_STORAGE: Record<string, string> = {
  brand_key: "brandKeyForFilters",
};

/**
 * The report kpi/get-execution-details runs for this screen. It is the
 * `report_key` every row of the response carries back, which is how the DPM
 * heat map + processing grid data is identified; the dynamic widgets pass the
 * equivalent value from their widget config (`list_of_api.api_key`), which a
 * static screen has none of.
 */
export const DPM_EXECUTION_API_KEY = 300221;

/**
 * The report kpi/get-execution-details runs for the Data Extract Monitor — the
 * DEM-Static equivalent of DPM_EXECUTION_API_KEY above, and the `report_key` its
 * rows carry back ("Daily file extract heatmap").
 *
 * The same key answers both of that screen's views, exactly as DPM's does:
 * report_typ 'D' for the daily view and 'H' for History (DPM_REPORT_TYP below).
 */
export const DEM_EXECUTION_API_KEY = 1002661;

/**
 * The report that answers with the tree's level ordering — which source types
 * come first, which sources inside each of them, and so on down.
 *
 * The same api_key the dynamic widgets use for this: status-overview.loadOrder()
 * and data-grid.loadOrder() both post it and feed the answer to ducharts as
 * `itemsOrder`. A static screen sorts its own tree with it instead (the component's
 * sortHeatNodes), but asks for it in exactly the same way — same endpoint, same
 * api_key, same `comment_typ` telling the backend which monitor is asking.
 *
 * One row comes back, with a `level_1` … `level_7` column per level, each a
 * comma-separated list of that level's values in display order.
 */
export const TREE_ORDER_API_KEY = 100280;

/** Values in display order, keyed by the record field (a HEAT_LEVELS entry) they
 *  order. A level the report said nothing about is simply absent, which the
 *  screen reads as "leave that level in the order the data arrived". */
export type StaticTreeOrder = Record<string, string[]>;

/**
 * The two things the dashboard card's Live and History buttons ask that one
 * api_key for. Live is the day-by-day view the screen has always shown;
 * History is the same data over a longer span, which DPM-Static draws as month
 * calendars rather than a date strip.
 */
export const DPM_REPORT_TYP = {
  live: "D",
  history: "H",
} as const;

export type DpmReportTyp = (typeof DPM_REPORT_TYP)[keyof typeof DPM_REPORT_TYP];

// ================================================== detail page behind a tile
/**
 * Where a static screen's tile double-click finds the detail page it opens.
 *
 * The dynamic status-overview widget reads this from its widget config
 * (`nav_configuration.controls[0]`, see status-overview.controlChartPagePopup):
 * s3JSON / dbJSON name the page's configuration JSON, and PagePopupComponent has
 * to fetch that before it can render a single tab — loadPageConfig() runs before
 * anything else. A static screen has no widget config to read, so the same
 * pointer is stated here instead.
 *
 * Which of the three routes is taken is decided exactly as the dynamic flow
 * decides it, by the portal's `selectConfig` (sessionStorage, 'S3' or 'DB'):
 *
 *   - S3 and isDashboardLevel 'yes' -> dbJSON.work_key is posted to
 *     api/get-page-access, and the pages that answers with are loaded;
 *   - S3 otherwise                  -> s3JSON.s3_bucket_nm + s3_file_path are
 *     read directly;
 *   - DB                            -> dbJSON.work_key + version_number are read
 *     as ConfigType 'DB'.
 *
 * Filling in both shapes is fine, and makes the screen work under either
 * setting. These are the values the production DEM status-overview widget
 * already carries — copy them from the page designer or the widget's config
 * JSON rather than re-deriving them.
 */
export interface StaticDetailPageRef {
  /**
   * The page name, and the popup's fallback title. MUST be non-empty: without it
   * PagePopupComponent ignores the pointer completely (its loadPageConfig() gates
   * on `routerPages && this.cardName`, and cardName is this field) and falls back
   * to resolving pages for the dashboard it was opened from — which for a static
   * dashboard is a key the config store has no pages for.
   */
  pageName: string;
  s3JSON: { s3_bucket_nm: string; s3_file_path: string };
  dbJSON: { work_key: any; version_number: any };
  /** 'yes' resolves the page through api/get-page-access instead of a direct read. */
  isDashboardLevel: string;
  /** Compose the popup title from navHeaderData rather than from pageName. */
  isNavSelected: boolean;
  /** Comma-separated navHeaderData keys that title is composed from. */
  navpageName: string;
  navpageNameLeft: string;
  navpageNameRight: string;
  /**
   * Cell-context keys an "open the full page" jump would carry into
   * sessionStorage (router-handler.storingPayloadAndFilterForGrid, which
   * PagePopupComponent.navigateToDetailPage calls with the popup's extraPayload).
   * Stated here for the same reason as the rest: it is `payloadData` off the
   * dynamic widget's nav_configuration.
   */
  payloadData: string;
}

/**
 * DEM-Static's detail page. EMPTY UNTIL CONFIGURED — see StaticDetailPageRef for
 * what each field is and where to copy it from. isDetailPageConfigured() is what
 * the screen tests before opening, so an unfilled pointer means the double-click
 * does nothing (and says why in the console) rather than opening a popup that can
 * only fail.
 */
export const DEM_DETAIL_PAGE: StaticDetailPageRef = {
  pageName: "DEM - Details Page",
  s3JSON: { s3_bucket_nm: "dustage-analytics-config", s3_file_path: "du/analytics/config/DEM_details_page.json" },
  dbJSON: { work_key: "20240819034346", version_number: 1.0 },
  isDashboardLevel: "",
  isNavSelected: true,
  // Mirrors the control-chart widget's own page_header nav_header_var.
  navpageName: "source_name,file_type_nm,cycle_dt",
  navpageNameLeft: "",
  navpageNameRight: "",
  payloadData: "source_key,file_type_dim_key,cycle_dt,file_nm,day_of_the_week,refresh_no,show_grid_flg",
};

/** Whether a pointer names a page PagePopupComponent can actually load. */
export function isDetailPageConfigured(
  ref: StaticDetailPageRef | null | undefined
): boolean {
  if (!ref?.pageName) return false;
  const viaS3 = !!(ref.s3JSON?.s3_bucket_nm && ref.s3JSON?.s3_file_path);
  const viaDb = !!ref.dbJSON?.work_key;
  return viaS3 || viaDb;
}

/**
 * A static dashboard's page key: its dashboard key with a two-digit page suffix
 * appended.
 *
 * This is how the portal's own page keys are built. Dashboard 20241125043213's
 * second page is 2024112504321321 — a 14-digit dashboard key plus "21" — which
 * pages.component.ts still filters by literal. A static dashboard has no page row to
 * read a key from, so it mints one in that same shape rather than inventing another.
 *
 * CONCATENATED, never added. The keys are timestamps (YYYYMMDDHHmmss), so adding 11
 * to one lands inside the seconds field and yields another dashboard's key eleven
 * seconds later; appending leaves the 14 digits intact and makes a 16-digit page key
 * like every other. `20260731014118` + page 1 -> `2026073101411811`.
 *
 * The suffix is positional and 1-based: page 1 is "11", page 2 "21", page 3 "31" —
 * the page number then a literal 1. Both static screens are single-page today, so
 * both take the default. A static dashboard that grows a second page passes 2.
 */
export function staticPageKey(dashboardKey: any, page = 1): string {
  return `${dashboardKey}${page}1`;
}

/** What loadDynamicFilters() reports back per filter. */
export interface DpmDynamicFilterState {
  def: DpmDynamicFilterDef;
  options: DpmFilterOption[];
  /** Chosen option keys (request values), as returned by the caller's picker. */
  selected: any[];
}

/** Picks the selection for a filter once its options are in. Supplied by the
 *  caller so the "default vs last used" policy stays with the screen. */
export type DpmSelectionPicker = (
  def: DpmDynamicFilterDef,
  options: DpmFilterOption[]
) => any[];

// ================================================================== bookmarks
/**
 * One dynamic filter's selection, as a bookmark carries it.
 *
 * Both halves are saved because either can go stale on its own: a request value
 * is the stable identifier and is what the screen matches on first, but a brand
 * re-keyed in the source system would strand the bookmark — so the label is kept
 * as the fallback match. (The chips key off labels too, which is what the
 * persisted-per-dashboard state in localStorage matches on.)
 */
export interface StaticBookmarkFilter {
  /** Request values, e.g. brand_key. */
  keys: any[];
  /** Display values, in the same order. */
  labels: string[];
}

/**
 * What a static screen's dynamic filters are currently on, keyed by filter.
 *
 * Both of the things that remember a static screen's selection are built from
 * this — the bookmark the panel files (publishBookmarkState) and the user state
 * the screen keeps across a navigation (UserActivityService.setStaticFilters).
 * Extracted so the two cannot drift: they are the same question asked by two
 * callers, and a bookmark that recorded the selection differently from the user
 * state would restore a different screen than the one the user left.
 *
 * Keys AND labels, because they answer different failure modes. The keys are
 * what the next visit re-selects by; the labels are the fallback for a filter
 * whose API has since re-issued its values under new keys, where matching on the
 * key alone would silently drop back to the default.
 *
 * A filter with nothing selected is left OUT rather than saved empty. It has no
 * selection to carry — its call failed, or it offered nothing — and an empty
 * entry would assert a state the screen was never in, which on the way back in
 * reads as "this filter was deliberately cleared".
 */
export function dynamicFilterSnapshot(
  states: DpmDynamicFilterState[]
): Record<string, StaticBookmarkFilter> {
  const dynamic: Record<string, StaticBookmarkFilter> = {};
  states.forEach((s) => {
    if (!s.selected.length) return;
    const chosen = s.options.filter((o) =>
      s.selected.some((k) => String(k) === String(o.key))
    );
    dynamic[s.def.key] = {
      keys: [...s.selected],
      labels: chosen.map((o) => o.label),
    };
  });
  return dynamic;
}

/**
 * The filter selection a static dashboard's bookmark carries, keyed by
 * DpmDynamicFilterDef.key.
 *
 * Only the API-backed (dynamic) filters are in here. The screen's other chips are
 * distinct values OF the loaded records — reopening the bookmark refetches that
 * data and rebuilds them, so there is nothing about them worth carrying that the
 * data does not already say. Adding a dynamic filter to DPM_DYNAMIC_FILTERS /
 * DEM_DYNAMIC_FILTERS puts it in here with no further work: both the write
 * (publishBookmarkState) and the read (the component's pickDefaultSelection) walk
 * whatever the screen resolved.
 */
export interface StaticBookmarkState {
  dynamic: Record<string, StaticBookmarkFilter>;
}

// ============================================================== widget help text
// The static dashboards' per-widget help. Each screen ships its text as an asset
// (assets/help_info/dpm-static.json, dem-static.json) and imports it as a module —
// static content that is part of the build, so there is no request to fail and no
// loading state to hold. The lookup itself lives here because both screens need the
// identical behaviour and a second copy of it is a second thing to keep in step.
//
// This is the WIDGET-level help only: the popover behind each card's and each
// panel's Help icon. The page-level Help icon in the topnav is a different thing
// entirely — it fetches, because it also has to resolve the user-manual PDF link,
// and each screen configures it through its own `topnavHelpInfo`.

/**
 * One widget's help text.
 *
 * Deliberately the shape icon-chart's own `infoData` has, so a panel's entry can be
 * handed to its header as-is through the `staticInfo` input rather than translated.
 *
 * `text_typ` is carried because the JSON states it; every entry is "HTML" and every
 * popover binds the text with [innerHtml], so nothing branches on it.
 */
export interface HelpEntry {
  info_header: string;
  info_text: string;
  text_typ?: string;
}

/**
 * Normalised lookup key for a help header.
 *
 * Normalised because the headers are display strings and some carry real line
 * breaks — "Processing Status\r\n(Processed Vs Received)" in DPM's file, \n in
 * DEM's. Keying on the raw string would make every call site a whitespace-exact
 * transcription of one, where a stray space fails silently as a missing popover.
 */
const helpKey = (header: string): string =>
  header.replace(/\s+/g, " ").trim().toLowerCase();

/**
 * A `helpFor(header)` over one screen's help JSON.
 *
 * A factory rather than a shared map: the two screens have separate files, and
 * mixing them would let one screen answer with the other's text.
 *
 * The returned function gives null for a header the file has no entry for, rather
 * than a placeholder — a widget with no entry should offer no Help icon at all
 * instead of one that opens and says nothing.
 */
export function makeHelpLookup(
  entries: HelpEntry[]
): (header: string) => HelpEntry | null {
  const byKey = new Map(entries.map((h) => [helpKey(h.info_header), h]));
  return (header: string) => byKey.get(helpKey(header)) || null;
}

// ===========================================================================
// Cross-filtering (Qlik / Power BI style visual interaction)
//
// Both static screens let a visual be clicked to narrow the page, the way a Qlik
// sheet or a Power BI report does. The two tools disagree about what a click
// should mean, and both readings are useful here, so the screens offer both and
// the modifier key picks:
//
//   click        -> cross-HIGHLIGHT. Nothing is filtered out. The matching parts
//                   of every widget stay saturated and the rest dim, so a share
//                   is read against its whole (Power BI's behaviour). Transient:
//                   the next click, Escape, or a click on empty space drops it.
//   ctrl/cmd+click -> cross-FILTER. The click is committed as a real selection:
//                   the dataset is narrowed, every widget re-aggregates, and a
//                   removable chip appears in the filter bar (Qlik's behaviour).
//                   Persisted with the rest of the screen's filter state.
//
// Both are expressed as the one StaticSelection below, so a highlight and a
// committed filter can never disagree about which records a click means — the
// only difference is whether the predicate narrows `viewData` or dims what is
// drawn from it.
// ===========================================================================

/**
 * What a selection matches a record on.
 *
 * `status`  — the record's canonical file_status (the KPI status cards, the
 *             donut's status arcs, the grid's status columns).
 * `flag`    — a 'Y'/'N' record column, named by `value`. The cards that count a
 *             flag rather than a status (DEM's Delayed / Interim Sent, DPM's
 *             Delayed) cut across the statuses, which is exactly why they cannot
 *             be expressed as one.
 * `count`   — a numeric record column, named by `value`, matched on being
 *             non-zero. DPM's Error and Success cards count records inside a
 *             file rather than files, so "show me the ones contributing" is a
 *             test on the column, not on the file's status.
 * `notStatus` — every record EXCEPT one status. DPM's Received is defined that
 *             way and cannot be defined any other: it is "expected minus
 *             missing", so there is no `file_status` value to match on.
 * `path`    — a heat-map / grid tree node, `value` being the node's full path id.
 *             Matches the node and everything under it.
 * `day`     — one ISO day, from a date column header or a calendar tile.
 */
export type StaticSelectionKind =
  | "status"
  | "notStatus"
  | "flag"
  | "count"
  | "path"
  | "day";

/**
 * One cross-filter selection, made by clicking a visual rather than a chip.
 *
 * Deliberately serialisable — no predicate function on the object. The screens
 * persist their selections alongside the filter chips (localStorage), and a
 * closure cannot survive JSON. Each component turns one of these back into a row
 * predicate through its own `selectionTest()`, which is also where the
 * screen-specific column names live.
 */
export interface StaticSelection {
  /**
   * Stable identity, `${kind}:${value}`. Clicking the same visual twice toggles
   * the selection off, which is what this is compared by.
   */
  id: string;
  /**
   * Which "field" this selection belongs to, for combining (see
   * combineSelections). Selections sharing a group are OR'd; groups are AND'd —
   * Qlik's associative rule, where picking two values of one field widens and
   * picking across two fields narrows.
   */
  group: string;
  kind: StaticSelectionKind;
  /** The status, column name, node path or ISO day this matches on. */
  value: string;
  /**
   * The RECORD COLUMN this selection is about — `file_status` for a status, the
   * tree level's key for a path, the flag's own column for a flag.
   *
   * What routes a selection to the filter badge that already covers it. Both
   * screens' filter chips are keyed by record column too (FilterDef.key), so a
   * selection whose field names a chip is expressed by narrowing THAT chip
   * rather than by adding a second one beside it: clicking the "Status" row in
   * the tree moves the File Type badge to 1/10, which is where a user would look
   * for that filter and where they would go to undo it.
   *
   * Only a selection with no chip to its name — the flag-backed KPI figures, and
   * a tree level the screen offers no filter for — stays a standalone chip. ''
   * for a selection that is not about a column at all: a day, which the date
   * badge covers instead.
   */
  field: string;
  /** Chip caption, and the wording of the "selected" title on the visual. */
  label: string;
  /** Chip tint — the colour the visual that produced it wears. '' for none. */
  color: string;
}

/**
 * The widget currently in selection mode, and what has been picked in it.
 *
 * Qlik's model, and the reason picks are held here rather than committed as they
 * are made: a selection is built up inside ONE object — click several bars, several
 * legend entries, several tree rows — and only becomes a filter when it is
 * confirmed. Until then the rest of the sheet previews it (matching data lit,
 * everything else dimmed) and the object carries a small toolbar offering the two
 * ways out.
 *
 * One widget at a time, deliberately. Picking in a second object confirms the
 * first, so there is never a question of which set a ✓ applies to — and never two
 * competing dimming rules on screen at once.
 */
export interface StaticPendingSelection {
  /** Which widget owns the mode: 'kpi' | 'donut' | 'heat' | 'grid'. */
  widget: string;
  /** The picked selections, in click order. Never empty — an empty pick list
   *  clears the mode instead of leaving an object outlined with nothing in it. */
  items: StaticSelection[];
}


/**
 * Fold a selection list into one row predicate.
 *
 * OR within a group, AND across groups. `test` resolves a selection to its
 * predicate and is passed in because the column names behind a kind are the
 * calling screen's, not this module's.
 *
 * An empty list answers a predicate that accepts everything, so callers never
 * have to special-case "nothing selected".
 */
export function combineSelections(
  selections: StaticSelection[],
  test: (sel: StaticSelection) => (record: any) => boolean
): (record: any) => boolean {
  if (!selections.length) return () => true;

  // Grouped once here rather than per row: this predicate runs across the whole
  // dataset on every filter change, and re-bucketing inside it would make the
  // grouping O(rows x selections) for an answer that cannot change between rows.
  const groups = new Map<string, ((record: any) => boolean)[]>();
  selections.forEach((sel) => {
    const bucket = groups.get(sel.group);
    if (bucket) bucket.push(test(sel));
    else groups.set(sel.group, [test(sel)]);
  });

  const folded = [...groups.values()];
  return (record: any) =>
    folded.every((tests) => tests.some((t) => t(record)));
}

/**
 * Does `path` name this node or something beneath it?
 *
 * Node ids are their labels joined with '/' (see each screen's buildHeatmap), so
 * a descendant is a prefix match on a whole segment — the trailing-slash test is
 * what stops "Sanofi" from also claiming a sibling called "Sanofi US".
 *
 * A label containing '/' would make its own id ambiguous, which is a property of
 * the id scheme the trees already use rather than of this test.
 */
export function pathMatches(nodeId: string, recordPath: string): boolean {
  return recordPath === nodeId || recordPath.startsWith(nodeId + "/");
}

@Injectable()
export class DpmStaticService {
  constructor(
    private apiService: ApiService,
    private sessionService: SessionService,
    private dashboardService: DashboardService,
    private filterService: FilterService,
    // Only for get-bookmark-by-id (loadBookmarkFilters). Reused rather than
    // re-declaring that endpoint here, so the static screens ask for a bookmark
    // exactly the way the rest of the portal does.
    private bookmarkService: BookmarkService
  ) {}

  /**
   * The six keys every request carries, read from the state the portal already
   * has: the route (dashboard / client / module), the client + employee session
   * ApiService itself posts with, and the signed-in user id the HTTP
   * interceptor sends as its `user_id` header — so an impersonated session
   * reports the impersonated user here too.
   *
   * page_key is the screen's own, minted by staticPageKey() and published onto
   * FilterService by publishPageKey() before the first request goes out — so it
   * arrives here through resolvePageKey()'s first source. It is left out entirely
   * if that has not happened, rather than sent as a guess.
   */
  buildContext(route: DpmStaticRouteKeys = {}): DpmStaticContext {
    const ctx: DpmStaticContext = {
      dashboard_key:
        route.dashboardKey || this.dashboardService.dashboardKey || null,
      client_key: route.clientKey || this.apiService.getClientKey() || null,
      user_id: this.sessionService.getUsername() || null,
      employee_key: this.apiService.getEmpKey() || null,
      module_key: route.moduleKey || this.apiService.getModuleKey() || null,
    };
    const pageKey = this.resolvePageKey();
    if (pageKey !== null && pageKey !== undefined && pageKey !== "") {
      ctx.page_key = pageKey;
    }
    return ctx;
  }

  private resolvePageKey(): any {
    const fromFilter = this.filterService.pageKey;
    if (fromFilter !== null && fromFilter !== undefined && fromFilter !== "") {
      return fromFilter;
    }
    const keys = this.dashboardService.getPageKey;
    return Array.isArray(keys) && keys.length ? keys[0] : null;
  }

  /**
   * Publish this screen's page key on FilterService, so everything that reads a
   * page key off the portal sends the same one for a static dashboard.
   *
   * FilterService.pageKey IS that channel: buildContext() takes it as
   * resolvePageKey()'s first source, and the topnav's page-level Help icon posts it
   * directly as `page_key` — topnav is never handed the context buildContext()
   * assembles, so writing the field is the only way to reach it.
   *
   * Overwrites unconditionally, which is the point. Whatever is in there belongs to
   * the page the portal was on BEFORE this one — pages.component sets it per tab —
   * so preferring the existing value would have a static screen quietly report the
   * last dynamic page it happened to follow. This screen knows its own page key, so
   * it states it.
   *
   * That does mean the key leaves this screen set on FilterService. Harmless in
   * practice: pages.component assigns it again on its own load, the same way it
   * already has to between two dynamic dashboards.
   *
   * Call before the first request — see the ngOnInit call sites.
   */
  publishPageKey(dashboardKey: any, page = 1): string {
    const key = staticPageKey(dashboardKey, page);
    this.filterService.pageKey = key;
    // DashboardService.getPageKey is the same key held per tab index, and it —
    // not FilterService — is what the shared side panels read: the Bookmarks
    // panel files a new bookmark under getPageKey[tab_id], and the static route
    // sends no tab_id, so that is index 0. Seeded for exactly the reason the
    // line above is written unconditionally — left alone it still holds the
    // pages of the last dynamic dashboard visited, so a bookmark taken here
    // would be stored against one of them. (status-overview's isolated page
    // popup seeds the array the same way.)
    this.dashboardService.getPageKey = [key];
    return key;
  }

  /**
   * Resolve every dynamic filter, in dependency order, and report what each one
   * offered and what was picked.
   *
   * Sequential rather than parallel on purpose: a filter's request carries the
   * selections of the filters it depends on, so those have to be settled first.
   * With one filter configured this is a single call; the shape is what lets
   * more be added without touching the caller.
   */
  loadDynamicFilters(
    ctx: DpmStaticContext,
    pickSelection: DpmSelectionPicker,
    // Which filters to resolve. Defaulted so DPM-Static's call is unchanged;
    // DEM-Static passes its own list, which is the whole reason this is a
    // parameter rather than the module constant read directly.
    defs: DpmDynamicFilterDef[] = DPM_DYNAMIC_FILTERS
  ): Observable<DpmDynamicFilterState[]> {
    const selections: Record<string, any[]> = {};
    const states: DpmDynamicFilterState[] = [];

    const step = (i: number): Observable<DpmDynamicFilterState[]> => {
      if (i >= defs.length) return of(states);
      const def = defs[i];
      return this.loadFilterOptions(def, ctx, selections, defs).pipe(
        switchMap((options) => {
          const selected = pickSelection(def, options) || [];
          selections[def.key] = selected;
          states.push({ def, options, selected });
          return step(i + 1);
        })
      );
    };

    return step(0);
  }

  /** One dynamic filter's option list. An failed call resolves to no options,
   *  which the caller reads as "this filter could not be loaded". */
  loadFilterOptions(
    def: DpmDynamicFilterDef,
    ctx: DpmStaticContext,
    selections: Record<string, any[]> = {},
    // The set `def.dependsOn` names are resolved against — the caller's list, so
    // a DEM filter can depend on a DEM filter. Defaulted for DPM-Static.
    defs: DpmDynamicFilterDef[] = DPM_DYNAMIC_FILTERS
  ): Observable<DpmFilterOption[]> {
    const body: any = { ...ctx };
    def.dependsOn.forEach((key) => {
      const dep = defs.find((d) => d.key === key);
      const chosen = selections[key];
      if (!dep || !chosen || !chosen.length) return;
      body[dep.requestField] = dep.single ? chosen[0] : chosen;
    });

    return this.apiService.post(def.endpoint, body).pipe(
      map((res: any) => this.toOptions(res, def)),
      catchError((err) => {
        console.error(`DPM-Static: ${def.endpoint} failed`, err);
        return of([] as DpmFilterOption[]);
      })
    );
  }

  /**
   * The dashboard data, scoped to the dynamic filters' selections.
   *
   * `params` is what dynamicFilterParams() built from those selections
   * (brand_key today), so this call automatically widens as filters are added.
   *
   * `reportTyp` is what the dashboard card's Live / History buttons come down
   * to: the same api_key answers both, and this is the only thing that says
   * which one is being asked for.
   */
  getExecutionDetails(
    ctx: DpmStaticContext,
    params: Record<string, any> = {},
    reportTyp: DpmReportTyp = DPM_REPORT_TYP.live,
    // Which report to run. Defaulted to DPM's so its call is unchanged;
    // DEM-Static passes DEM_EXECUTION_API_KEY.
    apiKey: number = DPM_EXECUTION_API_KEY
  ): Observable<any[]> {
    const body: any = {
      ...ctx,
      api_key: apiKey,
      report_typ: reportTyp,
      ...params,
    };

    // apiService.executionUrl is the portal's single definition of the
    // execution endpoint — the dynamic widgets post to the same constant.
    return this.apiService.post(this.apiService.executionUrl, body).pipe(
      map((res: any) => (Array.isArray(res) ? res : [])),
      catchError((err) => {
        console.error(
          `Static dashboard: kpi/get-execution-details failed (api_key ${apiKey})`,
          err
        );
        return of([] as any[]);
      })
    );
  }

  /**
   * The display order of the tree's levels — see TREE_ORDER_API_KEY.
   *
   * `levels` is the screen's hierarchy top-down (its HEAT_LEVELS), which is what
   * maps the response's positional columns onto record fields: level_1 is the
   * first level, level_2 the second, and so on. The dynamic widgets do the same
   * join against their widget config's l1_col … l7_col, whose
   * `api_resp_column_nm`s are that same list by another name.
   *
   * `commentTyp` is which monitor is asking ("DPME" / "DEM" for the two static
   * screens), the one parameter that changes the answer — status-overview sends
   * its config's `tree_order_type` or `type_of_page.name` here, and each static
   * screen sends the `type_of_page.name` on its own commentItem.
   *
   * Scoped by the same selections as the data (`params`, i.e. the brand), because
   * the order is per brand like everything else on the screen. report_typ is the
   * Live value in both views deliberately: what comes back describes the
   * hierarchy, not the date range drawn over it, so History would only be asking
   * the same question in a way the report may have no row for.
   *
   * A failure resolves to {} rather than erroring — the tree then keeps the order
   * the data arrived in, which is what it did before this call existed.
   */
  getTreeOrder(
    ctx: DpmStaticContext,
    levels: string[],
    commentTyp: string,
    params: Record<string, any> = {}
  ): Observable<StaticTreeOrder> {
    const body: any = {
      ...ctx,
      api_key: TREE_ORDER_API_KEY,
      report_typ: DPM_REPORT_TYP.live,
      comment_typ: commentTyp,
      ...params,
    };

    return this.apiService.post(this.apiService.executionUrl, body).pipe(
      map((res: any) => this.toTreeOrder(res, levels)),
      catchError((err) => {
        console.error(
          `Static dashboard: tree order failed (api_key ${TREE_ORDER_API_KEY}, ${commentTyp})`,
          err
        );
        return of({} as StaticTreeOrder);
      })
    );
  }

  /** The one response row's level_1 … level_n columns, split and keyed by the
   *  level they belong to. Values are trimmed because they are matched against
   *  record values, which the screen trims as it reads them. */
  private toTreeOrder(res: any, levels: string[]): StaticTreeOrder {
    const row = Array.isArray(res) ? res[0] : res;
    const order: StaticTreeOrder = {};
    if (!row) return order;

    levels.forEach((dim, i) => {
      const raw = row[`level_${i + 1}`];
      if (typeof raw !== "string") return;
      const values = raw
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean);
      // An empty column is not an order — leaving the key out is what tells the
      // screen to leave that level alone rather than rank every value as unknown.
      if (values.length) order[dim] = values;
    });

    return order;
  }

  /**
   * A selection this filter's dimension already has elsewhere in the portal
   * (Brand is picked once per module and shared), or null when there is none.
   */
  sharedSelection(def: DpmDynamicFilterDef): any {
    const storageKey = SHARED_SELECTION_STORAGE[def.requestField];
    if (!storageKey) return null;
    try {
      const raw = sessionStorage.getItem(storageKey);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  // ---------------------------------------------------------------- bookmarks
  /**
   * Hand the shared Bookmarks panel this screen's filter selection, so a bookmark
   * filed from here carries it.
   *
   * FilterService.staticBookmarkState IS that channel — see the field. The panel
   * builds `bookmark_value` from `badges`, which a static screen never fills, so
   * without this a static bookmark saves the filters of whatever dynamic page the
   * session visited before it and restores nothing.
   *
   * Called on every load and after every filter change (the component's
   * seedFilterState), because the panel reads the value at the moment the user
   * clicks Add — there is no notification to subscribe to.
   */
  publishBookmarkState(states: DpmDynamicFilterState[]): void {
    const dynamic = dynamicFilterSnapshot(states);
    this.filterService.staticBookmarkState = {
      // What the panel prints beside the bookmark. The screen's own count, not a
      // badge count: these are the filters it actually saved.
      filterCount: Object.keys(dynamic).length,
      value: { dynamic } as StaticBookmarkState,
    };
  }

  /** Stop contributing to bookmarks — the screen is going away. Left set, the
   *  next dynamic page's bookmarks would be filed as this screen's. */
  clearBookmarkState(): void {
    this.filterService.staticBookmarkState = null;
  }

  /**
   * The filter selection saved in a bookmark, or null when there is none to
   * apply — no key, a key that is not one, a request that failed, or a bookmark
   * taken before a screen carried its filters.
   *
   * Its own request rather than anything the Bookmarks panel already holds: the
   * panel is a side window that may never have been opened in this session, and on
   * a static route pages.component — which is what fetches a bookmark for the
   * dynamic flow — is not in the picture at all.
   *
   * `of(null)` on the no-key path so the caller can keep one chain for both cases
   * (see the component's loadDashboard).
   */
  loadBookmarkFilters(
    bookmarkKey: any
  ): Observable<StaticBookmarkState | null> {
    if (!bookmarkKey || isNaN(Number(bookmarkKey))) return of(null);
    return this.bookmarkService.bookMarkGetBaseQuery(bookmarkKey).pipe(
      map((res: any) => this.readBookmarkFilters(res?.[0]?.bookmark_value)),
      catchError((err) => {
        console.error(
          `Static dashboard: could not read bookmark ${bookmarkKey}`,
          err
        );
        return of(null);
      })
    );
  }

  /** Dig the saved selection out of a bookmark_value. Both levels arrive as JSON
   *  strings through the API but are tolerated as objects too, since the panel
   *  holds the same value un-stringified before it is posted. */
  private readBookmarkFilters(raw: any): StaticBookmarkState | null {
    const value = this.parseMaybeJson(raw);
    const state = this.parseMaybeJson(value?.[STATIC_BOOKMARK_FIELD]);
    return state?.dynamic ? (state as StaticBookmarkState) : null;
  }

  private parseMaybeJson(value: any): any {
    if (!value) return null;
    if (typeof value !== "string") return value;
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }

  /** Response rows -> options, dropping blanks and duplicate labels (the chip
   *  list keys off the label, so two rows sharing one would collide). */
  private toOptions(res: any, def: DpmDynamicFilterDef): DpmFilterOption[] {
    const rows = Array.isArray(res) ? res : res ? [res] : [];
    const seen = new Set<string>();
    const options: DpmFilterOption[] = [];
    rows.forEach((row: any) => {
      const label = row == null ? "" : String(row[def.labelField] ?? "").trim();
      if (!label || label === "null" || seen.has(label)) return;
      seen.add(label);
      options.push({ key: row[def.valueField], label, raw: row });
    });
    return options;
  }
}
